import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import styled from 'styled-components';

import { SegmentedChecksFilter } from 'dashboard/components/ChecksFilter';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Pagination from 'dashboard/components/Pagination';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { optionWithSubtextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Backdrop, Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { Verification } from 'dashboard/models/verification';
import { VerificationTemplateFilterOption } from 'dashboard/models/verification-template';
import {
  CheckFilterValues,
  VerificationsFilterParams,
} from 'dashboard/redux/actions/verifications';
import VerificationsTable from 'dashboard/views/DashboardVerificationsView/VerificationsTable';
import Card from 'design-system/components/Card';
import { GOVERNMENT_ID_DISPLAY_STRINGS } from 'lib/constants/governmentIds';
import { COUNTRIES } from 'lib/constants/locales';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { nameDisplay } from 'lib/utils/template';
import { VerificationStatus } from 'lib/verification';

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: VerificationStatus }> = [
  { label: 'Initiated', value: VerificationStatus.Initiated },
  { label: 'Confirmed', value: VerificationStatus.Confirmed },
  { label: 'Submitted', value: VerificationStatus.Submitted },
  { label: 'Passed', value: VerificationStatus.Passed },
  { label: 'Requires Retry', value: VerificationStatus.RequiresRetry },
  { label: 'Failed', value: VerificationStatus.Failed },
  { label: 'Skipped', value: VerificationStatus.Skipped },
  { label: 'Canceled', value: VerificationStatus.Canceled },
];

const GOVERNMENT_ID_FILTER_OPTIONS: Array<{ label: string; value: string }> = Object.entries(
  GOVERNMENT_ID_DISPLAY_STRINGS,
).map((entry) => {
  return {
    label: entry[1],
    value: entry[0],
  };
});

const GOV_ID_ISSUING_COUNTRY_FILTER_OPTIONS: Array<{ label: string; value: string }> =
  Object.entries(COUNTRIES).map((entry) => {
    return {
      label: entry[1],
      value: entry[0],
    };
  });

export const TYPE_FILTER_OPTIONS: Array<{
  label: string;
  value: Verification['type'];
}> = Object.entries(VERIFICATION_TYPE_NAMES_SHORT)
  .filter(
    ([verificationType, _label]) =>
      // TODO: [amok] Don't expose Verification::CertificateKorea until production ready
      ![
        ApiModel.VerificationCertificateKorea,
        ApiModel.VerificationTemplateDatabaseAadhaar,
        ApiModel.VerificationDigitalIdFinnishTrustNetwork,
        ApiModel.VerificationDigitalIdOneId,
        ApiModel.VerificationDigitalIdSingpass,
        ApiModel.VerificationDigitalIdUkSharecode,
      ].includes(verificationType as Verification['type']),
  )
  .map(([verificationType, label]) => ({
    label,
    value: verificationType as Verification['type'],
  }))
  .sort((t1, t2) => t1.label.localeCompare(t2.label));

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

export interface DashboardVerificationsViewProps {
  verifications: Verification[];
  tags: Tag[];
  loadingVerifications: boolean;
  loadingTags: boolean;
  loadingVerificationTemplateFilterOptions: boolean;
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: VerificationsFilterParams;
  pageSize: number;
  availableSubdivisions: Array<{ label: string; value: string }>;
  inquiryTemplates: (InquiryTemplate | Template)[];
  verificationTemplateFilterOptions: VerificationTemplateFilterOption[];
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleChecksFilterUpdate: (checks: CheckFilterValues[]) => void;
  handleStatusFilterUpdate: (status: VerificationStatus[]) => void;
  handleTagIdFilterUpdate: (tagIds: string[]) => void;
  handleIssuingCountriesFilterUpdate: (countries: string[]) => void;
  handleIssuingSubdivisionsFilterUpdate: (tagIds: string[]) => void;
  handleGovIdTypeFilterUpdate: (governmentIdTypes: string[]) => void;
  handleTypeFilterUpdate: (type: Verification['type'][]) => void;
  handleInquiryTemplateFilterUpdate: (inquiryTemplateIds: string[]) => void;
  handleVerificationTemplateFilterUpdate: (verificationTemplateIds: string[]) => void;
}

const DashboardVerificationsView = ({
  verifications,
  tags,
  loadingVerifications,
  loadingTags,
  loadingVerificationTemplateFilterOptions,
  paginationParams,
  sortParams,
  filters,
  pageSize,
  availableSubdivisions,
  inquiryTemplates,
  verificationTemplateFilterOptions,
  handleDateUpdate,
  handleOmniSearch,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  handleChecksFilterUpdate,
  handleStatusFilterUpdate,
  handleTagIdFilterUpdate,
  handleIssuingCountriesFilterUpdate,
  handleIssuingSubdivisionsFilterUpdate,
  handleGovIdTypeFilterUpdate,
  handleTypeFilterUpdate,
  handleInquiryTemplateFilterUpdate,
  handleVerificationTemplateFilterUpdate,
}: DashboardVerificationsViewProps) => {
  const {
    createdAtStart,
    createdAtEnd,
    status,
    type,
    tagIds,
    omni,
    issuingCountries,
    issuingSubdivisions,
    governmentIdTypes,
    checks,
    inquiryTemplateIds,
    verificationTemplateIds,
  } = filters;
  const tagIdFilterOptions = tags.map((tag) => ({ label: tag.attributes.name, value: tag.id }));

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });

  const governmentIdIssuingCountryFilterLabelTextMapper = useLabelTextMapper(
    GOV_ID_ISSUING_COUNTRY_FILTER_OPTIONS,
    'countries',
    {
      emptyIsAll: true,
    },
  );

  const governmentIdTypeFilterLabelTextMapper = useLabelTextMapper(
    GOVERNMENT_ID_FILTER_OPTIONS,
    'ID types',
    {
      emptyIsAll: true,
    },
  );

  const tagIdFilterLabelTextMapper = useLabelTextMapper(tagIdFilterOptions, 'tags', {
    emptyIsAll: true,
  });

  const typeFilterLabelTextMapper = useLabelTextMapper(TYPE_FILTER_OPTIONS, 'types', {
    emptyIsAll: true,
  });

  const governmentIdIssuingSubdivisionsFilterLabelTextMapper = useLabelTextMapper(
    availableSubdivisions,
    'subdivisions',
    {
      emptyIsAll: true,
    },
  );

  const inquiryTemplateFilterOptions = inquiryTemplates.map((inquiryTemplate) => ({
    label: `${nameDisplay(inquiryTemplate)} (${inquiryTemplate.id})`,
    customLabel: optionWithSubtextMapper(nameDisplay(inquiryTemplate), inquiryTemplate.id),
    value: inquiryTemplate.id,
  }));

  const inquiryTemplateFilterLabelTextMapper = useLabelTextMapper(
    inquiryTemplateFilterOptions,
    'inquiry templates',
    {
      emptyIsAll: true,
    },
  );

  const displayVerificationTemplateFilterOptions = verificationTemplateFilterOptions.map(
    ({ name, id }) => ({
      label: `${name} (${id})`,
      customLabel: optionWithSubtextMapper(name, id),
      value: id,
    }),
  );

  const verificationTemplateFilterLabelTextMapper = useLabelTextMapper(
    displayVerificationTemplateFilterOptions,
    'verification templates',
    {
      emptyIsAll: true,
    },
  );

  if (loadingTags || loadingVerificationTemplateFilterOptions) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  }

  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const filterConfig: FilterConfig[] = [
    {
      id: 'dateCreated',
      name: 'Date created',
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
    },
    {
      id: 'type',
      htmlId: 'verifications-view__type',
      name: 'Type',
      type: 'multiselect-legacy',
      selectOptionValues: TYPE_FILTER_OPTIONS,
      labelTextMapper: typeFilterLabelTextMapper,
      onChange: handleTypeFilterUpdate,
      value: type || [],
    },
    {
      id: 'verificationChecks',
      htmlId: 'verifications-view__checks',
      name: 'Verification checks',
      type: 'custom',
      filterElement: (
        <SegmentedChecksFilter
          onChange={handleChecksFilterUpdate}
          dataTest='verifications__select--checks'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={checks}
          size='md'
        />
      ),
      onDelete: () => handleChecksFilterUpdate([]),
      hasValue: checks && checks.length > 0,
    },
    {
      id: 'status',
      htmlId: 'verifications-view__status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      onChange: handleStatusFilterUpdate,
      value: status || [],
    },
    {
      id: 'tag',
      htmlId: 'verifications-view__tags',
      name: 'Tag',
      type: 'multiselect-legacy',
      enabled: tagIdFilterOptions.length >= 1,
      selectOptionValues: tagIdFilterOptions,
      labelTextMapper: tagIdFilterLabelTextMapper,
      onChange: handleTagIdFilterUpdate,
      value: tagIds ? tagIds : [],
    },
    {
      id: 'govIdType',
      htmlId: 'verifications-view__gov-id-type',
      name: 'Government ID Type',
      type: 'multiselect-legacy',
      enabled: Boolean(type?.includes(ApiModel.VerificationGovernmentId)),
      selectOptionValues: GOVERNMENT_ID_FILTER_OPTIONS,
      labelTextMapper: governmentIdTypeFilterLabelTextMapper,
      onChange: handleGovIdTypeFilterUpdate,
      value: governmentIdTypes ? governmentIdTypes : [],
    },
    {
      id: 'issuingCountry',
      htmlId: 'verifications-view__issuing-country',
      name: 'Issuing country',
      type: 'multiselect-legacy',
      enabled: Boolean(type?.includes(ApiModel.VerificationGovernmentId)),
      selectOptionValues: GOV_ID_ISSUING_COUNTRY_FILTER_OPTIONS,
      labelTextMapper: governmentIdIssuingCountryFilterLabelTextMapper,
      onChange: handleIssuingCountriesFilterUpdate,
      value: issuingCountries ? issuingCountries : [],
    },
    {
      id: 'subdivision',
      htmlId: 'verifications-view__issuing-subdivision',
      name: 'Issuing subdivision',
      type: 'multiselect-legacy',
      enabled: Boolean(
        type?.includes(ApiModel.VerificationGovernmentId) &&
          availableSubdivisions.length > 0 &&
          issuingCountries &&
          issuingCountries.length > 0,
      ),
      selectOptionValues: availableSubdivisions,
      labelTextMapper: governmentIdIssuingSubdivisionsFilterLabelTextMapper,
      onChange: handleIssuingSubdivisionsFilterUpdate,
      value: issuingSubdivisions ? issuingSubdivisions : [],
    },
    {
      id: 'inquiryTemplates',
      htmlId: 'verifications-view__inquiry-templates',
      name: 'Inquiry templates',
      type: 'multiselect-legacy',
      enabled: inquiryTemplates.length > 1,
      selectOptionValues: inquiryTemplateFilterOptions,
      labelTextMapper: inquiryTemplateFilterLabelTextMapper,
      onChange: handleInquiryTemplateFilterUpdate,
      value: inquiryTemplateIds ? inquiryTemplateIds : [],
      minWidth: '250px',
    },
    {
      id: 'verificationTemplates',
      htmlId: 'verifications-view__verification-template_filter_options',
      name: 'Verification templates',
      type: 'multiselect-legacy',
      enabled: verificationTemplateFilterOptions.length > 1,
      selectOptionValues: displayVerificationTemplateFilterOptions,
      labelTextMapper: verificationTemplateFilterLabelTextMapper,
      onChange: handleVerificationTemplateFilterUpdate,
      value: verificationTemplateIds ? verificationTemplateIds : [],
      minWidth: '250px',
    },
  ];

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Verifications' />
        <Section data-test='verifications-view'>
          <Card>
            <Card.Body>
              <TableTitleContainer>
                <Card.Title>Verifications</Card.Title>
              </TableTitleContainer>
              <TableControls
                searchProps={{
                  id: 'verifications__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Search by verification ID, or name',
                }}
                filters={filterConfig}
              />
              <VerificationsTable
                verifications={verifications}
                sortParams={sortParams}
                isLoading={loadingVerifications}
                hasUsedFilters={hasUsedFilters}
                handleSortUpdate={handleSortUpdate}
              />
              {verifications.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardVerificationsView;
